import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
	CSVProtocolTypes,
	EmailProtocolTypes,
	ServiceProtocolTypes,
	SQLProtocolTypes
} from 'src/models/productImport/enums/protocolTypes.enum';
import {CompressionTypes} from 'src/models/productImport/enums/compressionTypes.enum';
import {FileTypes} from 'src/models/productImport/enums/fileTypes.enum';
import {Encodings} from 'src/models/productImport/enums/encodings.enum';
import {StepModel} from 'src/models/productImport/step.model';
import {PreviewDataFormEntry} from 'src/models/productImport/previewData.model';
import {ImportConfigValidators} from 'src/modules/productImport/validators/import-config-validators';
import {ImportConfigDetailModel} from 'src/models/productImport/config/importConfig.model';
import {ImporterConfigMapping, MappingEntry, SubMappingEntry} from 'src/models/productImport/mapping/mapping.config.model';
import {FormHttpDownloadParameter, HttpDownloadParameter} from 'src/models/productImport/config/http.config.model';
import {MultiFile} from 'src/models/productImport/config/compression.config.model';
import {FieldFilterConfigModel} from 'src/models/productImport/mapping/field.filter.config.model';
import {FilterAssociation, FilterOperation, FilterType} from 'src/models/productImport/enums/filter.enums';
import {AbsenderConfig} from 'src/models/productImport/config/email.config.model';


@Injectable({
	providedIn: 'root'
})
export class ImportConfigFormService {

	constructor(private fb: UntypedFormBuilder) {
	}

	enableAllForms(steps: StepModel[]): void {
		steps.forEach((step: StepModel, index: number) => {
			step.stepForm.enable({emitEvent: false}); // emitEvent: false prevents change-listener to trigger on enable/disable
			if (index === 1) {
				// Keep FileType disabled
				step.stepForm.get('fileType')?.disable();
			}
		});
	}

	resetDirtyFlag(steps: StepModel[]): void {
		steps.forEach((step: StepModel) => {
			step.stepForm.markAsPristine();
		});
	}

	initStep1Form(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			title: ['', Validators.required],
			cron: this.fb.group({
				interval: ['', [Validators.min(15), Validators.pattern(/^[1-9]\d*$/)]],
				pattern: [''],
				isEnabled: [false]
			}, {
				// TODO: Validator fixen - der beißt sich mit dem Min-Validator (und ist nicht 100% korrekt)
				// validators: ImportConfigValidators.CronGroupValidator
			}),
		});
		if (details) {
			form.patchValue(details);
			form.disable();
		}
		return form;
	}

	initStep2Form(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			fileType: [FileTypes.CSV, Validators.required], // Only CSV in the beginning
			hostProtocol: [CSVProtocolTypes.HTTPS, Validators.required],
			compression: this.fb.group({
				type: [CompressionTypes.NONE, Validators.required],
				// "files" -> added only on type===CompressionTypes.ZIP OR if details are provided
			}),
			// "http", "ftp" and "smb" are more properties - filled/created via changeProtocolAndSubForm
		});

		if (details) {
			if (details.fileType === FileTypes.CSV) {
				this.addCsvConfigToStep2Form(form, details);
			}
			this.changeProtocolAndSubForm(form, details.hostProtocol, details);
			if (details.hostProtocol === CSVProtocolTypes.HTTP || details.hostProtocol === CSVProtocolTypes.HTTPS) {
				this.setDownloadParamsOnHttp(form.get('http'), details.http.downloadParameter);
			}
			if (details.compression.type === CompressionTypes.ZIP) {
				this.addCompressionFilesFormArray(form, details.compression?.files);
			}
			form.patchValue(details);
			form.disable();
		} else {
			// Default is CSV -> add csv-form
			this.addCsvConfigToStep2Form(form);
			this.changeProtocolAndSubForm(form, CSVProtocolTypes.HTTPS, details);
		}

		return form;
	}

	private addCsvConfigToStep2Form(form: UntypedFormGroup, details?: ImportConfigDetailModel): void {
		const csvFormGroup = this.fb.group({
			csvSeparator: ['', Validators.required],
			csvFirstRowIsHeader: [true, Validators.required],
			characterEncoding: [Encodings.UTF_8, Validators.required],
		});

		if (details) {
			csvFormGroup.patchValue(details.csv);
		}

		form.addControl('csv', csvFormGroup);
	}

	initStep3Form(formEnabled: boolean): UntypedFormGroup {
		const form = this.fb.group({
			identifier: ['', ImportConfigValidators.IdentifierValidator],
			mappings: this.fb.array([])
		});
		// Only use to determine ViewMode / Create-Mode
		// Form-Content is provided in step3-component
		if (!formEnabled) {
			form.disable();
		}

		return form;
	}

	initStep5Form(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({});
		if (details) {
			form.patchValue(details);
			form.disable();
		}
		return form;
	}

	changeProtocolAndSubForm(
		form: UntypedFormGroup,
		hostProtocol: CSVProtocolTypes | SQLProtocolTypes | ServiceProtocolTypes | EmailProtocolTypes,
		details?: ImportConfigDetailModel
	): void {
		if (form.disabled) {
			return;
		}
		form.patchValue({hostProtocol});
		switch (hostProtocol) {
			case CSVProtocolTypes.FTP:
			case CSVProtocolTypes.SFTP:
				form.setControl('ftp', this.initFTPImportProtocolForm(details));
				form.removeControl('http');
				form.removeControl('smb');
				form.removeControl('mysql');
				form.removeControl('service');
				form.removeControl('email');
				break;
			case CSVProtocolTypes.HTTP:
			case CSVProtocolTypes.HTTPS:
				form.setControl('http', this.initHTTPImportProtocolForm(details));
				form.removeControl('ftp');
				form.removeControl('smb');
				form.removeControl('mysql');
				form.removeControl('service');
				form.removeControl('email');
				break;
			case CSVProtocolTypes.SMB:
				form.setControl('smb', this.initSMBImportProtocolForm(details));
				form.removeControl('http');
				form.removeControl('ftp');
				form.removeControl('mysql');
				form.removeControl('service');
				form.removeControl('email');
				break;
			case SQLProtocolTypes.MYSQL:
				form.setControl('mysql', this.initMySQLImportProtocolForm(details));
				form.removeControl('http');
				form.removeControl('smb');
				form.removeControl('ftp');
				form.removeControl('service');
				form.removeControl('email');
				break;
			case ServiceProtocolTypes.DOCKER:
				form.setControl('service', this.initServiceImportProtocolForm(details));
				form.removeControl('http');
				form.removeControl('smb');
				form.removeControl('ftp');
				form.removeControl('mysql');
				form.removeControl('email');
				break;
			case EmailProtocolTypes.MAIL:
				form.setControl('email', this.initEmailImportProtocolForm(details));
				form.removeControl('http');
				form.removeControl('smb');
				form.removeControl('ftp');
				form.removeControl('mysql');
				form.removeControl('service');
				break;
			default:
				throw Error(`SubForm: Unknown Protocoltype ${hostProtocol}`);
		}

	}

	initFTPImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [CSVProtocolTypes.SFTP, Validators.required],
			hostAddress: ['', Validators.required],
			hostUser: ['', Validators.required],
			hostPassword: ['', Validators.required],
			hostPort: ['', Validators.required],
			hostRemoteDir: [''],
			hostRemoteFile: ['', Validators.required],
			explicitTls: [false],
		});

		if (details && details.ftp) {
			form.patchValue(details.ftp);
		}

		return form;
	}

	initHTTPImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [CSVProtocolTypes.HTTPS, Validators.required],
			hostAddress: ['', Validators.required],
			hostUser: [''],
			hostPassword: [''],
			formDownloadParams: this.fb.array([this.createNewDownloadParameterFormGroup()]),
		});
		if (details && details.http) {
			form.patchValue(details.http);
		}

		return form;
	}

	initSMBImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [CSVProtocolTypes.SMB, Validators.required],
			hostAddress: ['', Validators.required],
			hostUser: ['', Validators.required],
			hostPassword: ['', Validators.required],
			hostPort: ['', Validators.required],
			hostRemoteDir: [''],
		});
		if (details && details.smb) {
			form.patchValue(details.smb);
		}

		return form;
	}

	initMySQLImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [SQLProtocolTypes.MYSQL, Validators.required],
			hostAddress: ['', Validators.required],
			hostUser: ['', Validators.required],
			hostPassword: ['', Validators.required],
			hostPort: ['', Validators.required],
			hostDatabase: ['', Validators.required],
			hostView: ['', Validators.required],
		});
		if (details && details.mysql) {
			form.patchValue(details.mysql);
		}

		return form;
	}

	initServiceImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [ServiceProtocolTypes.DOCKER, Validators.required],
			dockerHost: ['', Validators.required],
			dataEndpoint: ['', Validators.required],
			fieldsEndpoint: ['', Validators.required],
			topic: ['', Validators.required],
		});
		if (details && details.service) {
			form.patchValue(details.service);
		}

		return form;
	}

	initEmailImportProtocolForm(details?: ImportConfigDetailModel): UntypedFormGroup {
		const form = this.fb.group({
			hostProtocol: [EmailProtocolTypes.MAIL, Validators.required],
			hostAddress: ['mail.your-server.de', Validators.required],
			hostPort: [993],
			emailAddress: ['secomp@code-plant.de', [Validators.required, Validators.email]],
			emailPassword: ['q2$GiLg][dMB', Validators.required],
			emailDir: ['INBOX'],
			emailView: [false],
			tls: [true],
			prompt: [''],
			// System-Prompt-Override: startet gesperrt (read-only Anzeige des
			// Standards), per Stift-Toggle editierbar. getRawValue() beim Save
			// nimmt auch disabled Controls mit.
			systemPrompt: [{value: '', disabled: true}],
			// FTP-Ablageziel der erzeugten OpenTrans-XML (pro Config). Leer = kein
			// FTP (nur S3). Wird durchgereicht: import-manager → mail-import →
			// Wire-Event → opentrans-exporter (FTP-Upload).
			// Kundennummer dieses Kunden im eigenen ERP — steht auf keinem Beleg,
			// wird als supplier_specific PARTY_ID in die OpenTrans-XML geschrieben.
			erpCustomerNumber: [''],
			ftpConfig: this.fb.group({
				host: [''],
				port: [21],
				user: [''],
				password: [''],
				dir: [''],
				tls: [false],
			}),
			absenders: this.fb.array([], Validators.minLength(1))
		});

		// Initialize absenders
		const absArr = form.get('absenders') as UntypedFormArray;
		if (details && details.email) {
			form.patchValue(details.email);
			const provided = (details.email as any).absenders as AbsenderConfig[] | undefined;
			if (provided && provided.length) {
				// ALLE Per-Absender-Felder wiederherstellen (withLlm/llmId gingen
				// vorher beim Reload verloren).
				provided.forEach((a) => absArr.push(this.createAbsenderFormGroup(
					a.sender, a.withPdf, a.withLlm, a.llmId || '', a.requireBillingAddress || false, a.requireCustomerOrderReference || false, a.requireEan || false)));
			} else {
				absArr.push(this.createAbsenderFormGroup());
			}
		} else {
			absArr.push(this.createAbsenderFormGroup());
		}

		return form;
	}


	getDetailModelFromStepForms(steps: StepModel[], details?: ImportConfigDetailModel): any {
		const createModel: ImportConfigDetailModel = steps.reduce((model: any, step: StepModel) => {
			return Object.assign(model, step.stepForm.getRawValue());
		}, details || {});

		createModel.cron.interval = +createModel.cron.interval;
		if (createModel.ftp) {
			createModel.ftp.hostPort = +createModel.ftp.hostPort;
		}
		if (createModel.smb) {
			createModel.smb.hostPort = +createModel.smb.hostPort;
		}
		if (createModel.mysql) {
			createModel.mysql.hostPort = +createModel.mysql.hostPort;
		}
		if (createModel.email) {
			createModel.email.hostPort = +createModel.email.hostPort;
		}
		if (createModel.http) {
			createModel.http.downloadParameter = this.mapFormDownloadParamsValuesToObject(createModel.http.formDownloadParams);
			// @ts-ignore
			delete createModel.http.formDownloadParams;
		}

		return createModel;
	}

	getMappingModelFromStep3Form(step3Form: UntypedFormGroup): any {
		const mappingModel: ImporterConfigMapping = step3Form.getRawValue();

		mappingModel.mappings.forEach((entry: MappingEntry) => {
			delete entry.sourceFieldExampleValue;
			delete entry.isIdentifier;
			// TODO: Select-Value für "null" fixen -> das hier ist n workaround
			if (entry.labelUuid === 'null') {
				entry.labelUuid = '';
			}
			// Reset only half filled entries
			if (!entry.labelUuid || !entry.dynamicFieldUuid) {
				entry.labelUuid = '';
				entry.dynamicFieldUuid = '';
			}
			// Sub-Mapping aufräumen: leere Zeilen (ohne Quellfeld) raus; ganz ohne
			// Sub-Zeilen auch sourceListPath nicht mitschicken (flacher Eintrag).
			if (entry.subMapping && entry.subMapping.length > 0) {
				entry.subMapping = entry.subMapping.filter((sub: SubMappingEntry) => {
					return !!sub.sourceFieldName && !!sub.dynamicFieldUuid;
				});
			}
			if (!entry.subMapping || entry.subMapping.length === 0) {
				delete entry.subMapping;
				delete entry.sourceListPath;
			}
		});

		return mappingModel;
	}

	createNewDownloadParameterFormGroup(): UntypedFormGroup {
		return this.fb.group({
			key: [''],
			value: ['']
		});
	}

	private mapFormDownloadParamsValuesToObject(formDownloadParams: FormHttpDownloadParameter[]): HttpDownloadParameter {
		// In the form, the download params have the form
		// 		[{key: 'test', value: 'abc'},
		// 		{key: 'anotherTest', value: 'def'}],
		// but we need them in the form of
		// 		{test: 'abc',
		// 	    anotherTest: 'def'}
		return formDownloadParams
			.filter((entry: FormHttpDownloadParameter) => {
				return entry.key; // Param-Name is sufficient
			})
			.reduce((model: HttpDownloadParameter, entry: FormHttpDownloadParameter) => {
				model[entry.key] = entry.value;
				return model;
			}, {} as HttpDownloadParameter); // Type is important on reduce with objects
	}


	createAbsenderFormGroup(sender: string = '', withPdf: boolean = false, withLlm: boolean = false, llmId: string = '',
							requireBillingAddress: boolean = false, requireCustomerOrderReference: boolean = false,
							requireEan: boolean = false): UntypedFormGroup {
		return this.fb.group({
			sender: [sender, Validators.required],
			withPdf: [withPdf],
			withLlm: [withLlm],
			llmId: [llmId],
			// Pro Absender: fehlende Rechnungsadresse flaggt die Order in den Review.
			requireBillingAddress: [requireBillingAddress],
			// Pro Absender: fehlende Kundenauftrag-Nr flaggt die Order in den Review.
			requireCustomerOrderReference: [requireCustomerOrderReference],
			requireEan: [requireEan],
		});
	}

	private setDownloadParamsOnHttp(form: AbstractControl | null, downloadParameter: HttpDownloadParameter): void {
		if (!downloadParameter) {
			return;
		}

		const array = form?.get('formDownloadParams') as UntypedFormArray;
		if (!array) {
			throw Error('Control formDownloadParams not found');
		}

		array.clear();

		const keys = Object.keys(downloadParameter);

		keys.forEach((key: string) => {
			const formParamGroup = this.createNewDownloadParameterFormGroup();

			formParamGroup.patchValue({
				key,
				value: downloadParameter[key]
			});

			array.push(formParamGroup);
		});
	}


	createZipFileFormGroup(formFile?: MultiFile): UntypedFormGroup {
		const form = this.fb.group({
			fileName: ['', Validators.required],
			primaryKey: ['']
		});

		if (formFile) {
			form.patchValue(formFile);
		}
		return form;
	}

	addCompressionFilesFormArray(form: UntypedFormGroup, files?: MultiFile[]): void {
		const compression = form.get('compression') as UntypedFormGroup;
		if (!compression) {
			throw Error(`No "compression"-Control in Form found: ${form}`);
		}

		const fileFormArray = this.fb.array([this.createZipFileFormGroup()]);
		if (files) {
			fileFormArray.clear();
			files.forEach((file: MultiFile) => {
				fileFormArray.push(this.createZipFileFormGroup(file));
			});
		}

		compression.addControl('files', fileFormArray);
	}

	removeCompressionFilesFormArray(form: UntypedFormGroup): void {
		const compression = form.get('compression') as UntypedFormGroup;
		if (!compression) {
			throw Error(`No "compression"-Control in Form found: ${form}`);
		}
		compression.removeControl('files');
	}

	fillMappingForm(form: UntypedFormGroup, previewData: PreviewDataFormEntry[], importerMapping: ImporterConfigMapping): void {
		const mappingArray = form.get('mappings') as UntypedFormArray;
		if (!mappingArray) {
			return;
		}
		mappingArray.clear();

		form.patchValue({identifier: importerMapping.identifier});

		previewData.forEach((entry: PreviewDataFormEntry) => {
			const mappingEntryFormGroup = this.fb.group({
				sourceFieldName: [{disabled: true, value: entry.key}],
				sourceFieldExampleValue: [{disabled: true, value: entry.value}],
				dynamicFieldUuid: [''],
				labelUuid: [''],
				isIdentifier: [importerMapping.identifier === entry.key],
				fieldFilterConfigs: this.fb.array([]),
				filterAssociation: [FilterAssociation.And],
				// Sub-Mapping für reference-Felder (Sub-Labels). Befüllt wenn das
				// Zielfeld ein reference-Feld ist; sonst leer.
				sourceListPath: [''],
				subMapping: this.fb.array([])
			});

			const mappingEntry = importerMapping?.mappings?.find((mentry: MappingEntry) => {
				// reference-Einträge matchen über sourceListPath (die Quell-Liste).
				return mentry.sourceFieldName === entry.key ||
					(!!mentry.sourceListPath && mentry.sourceListPath === entry.key);
			});

			if (mappingEntry) {
				const {dynamicFieldUuid = null, labelUuid = null} = mappingEntry; // Default to null (select empty-selection)
				mappingEntryFormGroup.patchValue({
					dynamicFieldUuid,
					labelUuid,
					filterAssociation: mappingEntry.filterAssociation,
					sourceListPath: mappingEntry.sourceListPath || ''
				});
				const filterFormArray = mappingEntryFormGroup.get('fieldFilterConfigs') as UntypedFormArray;
				this.addFieldFilterFormGroups(filterFormArray, mappingEntry.fieldFilterConfigs || []);

				// gespeichertes Sub-Mapping wiederherstellen
				const subArray = mappingEntryFormGroup.get('subMapping') as UntypedFormArray;
				(mappingEntry.subMapping || []).forEach((sub: SubMappingEntry) => {
					subArray.push(this.createSubMappingFormGroup(sub));
				});
			}

			mappingArray.push(mappingEntryFormGroup);
		});
	}

	// Eine Sub-Mapping-Zeile (Quellfeld im Listenelement → Sub-Label-Feld).
	createSubMappingFormGroup(sub?: Partial<SubMappingEntry>): UntypedFormGroup {
		return this.fb.group({
			sourceFieldName: [sub?.sourceFieldName || ''],
			labelUuid: [sub?.labelUuid || ''],
			dynamicFieldUuid: [sub?.dynamicFieldUuid || '']
		});
	}

	addFieldFilterFormGroups(filterFormArray: UntypedFormArray, configs: FieldFilterConfigModel[]): void {
		filterFormArray.clear();
		configs.forEach((filterConfig: FieldFilterConfigModel) => {
			const group = this.createFieldFilterFormGroup(filterConfig);
			filterFormArray.push(group);
		});
	}

	handleProductIdentifierUpdate(checked: boolean, changedEntryIndex: number, form: UntypedFormGroup): void {
		const mappingEntries = form.get('mappings') as UntypedFormArray;
		if (!mappingEntries) {
			throw Error('No mapping found in form');
		}

		mappingEntries.controls.forEach((entry: AbstractControl, index: number) => {
			entry.patchValue({isIdentifier: index === changedEntryIndex && checked});
		});

		const mappingControl = mappingEntries.controls[changedEntryIndex];

		if (mappingControl.get('isIdentifier')?.value === true) {
			form.patchValue({identifier: mappingControl.get('sourceFieldName')?.value});
		} else {
			form.patchValue({identifier: ''});
		}
	}

	changeFileType(form: UntypedFormGroup, value: FileTypes, importDetails?: ImportConfigDetailModel): void {
		form.patchValue({fileType: value});

		switch (value) {
			case FileTypes.CSV:
				this.addCsvConfigToStep2Form(form, importDetails);
				this.changeProtocolAndSubForm(form, CSVProtocolTypes.HTTPS, importDetails);
				break;
			case FileTypes.SQL:
				form.removeControl('csv');
				this.changeProtocolAndSubForm(form, SQLProtocolTypes.MYSQL, importDetails);
				break;
			case FileTypes.SERVICE:
				form.removeControl('csv');
				this.changeProtocolAndSubForm(form, ServiceProtocolTypes.DOCKER, importDetails);
				break;
			case FileTypes.EMAIL:
				form.removeControl('csv');
				this.changeProtocolAndSubForm(form, EmailProtocolTypes.MAIL, importDetails);
				break;
		}
	}


	createFieldFilterFormGroup(config?: FieldFilterConfigModel): UntypedFormGroup {
		const group = this.fb.group({
			type: [FilterType.Equals, Validators.required],
			operation: [FilterOperation.Equals, Validators.required],
			compareValue: ['', Validators.required]
		});
		if (config) {
			group.patchValue(config);
			this.updateValidatorsForFilterType(group);
		}
		return group;
	}

	private removeRequiredFromControl(controlKey: string, group: AbstractControl): void {
		const value = group.get(controlKey);
		if (!value) {
			throw Error(`no formControl '${controlKey}' found`);
		}
		value.setValidators(null);
		value.updateValueAndValidity();
	}

	private addRequiredToControl(controlKey: string, group: AbstractControl): void {
		const value = group.get(controlKey);
		if (!value) {
			throw Error(`no formControl '${controlKey}' found`);
		}
		value.setValidators(Validators.required);
		value.updateValueAndValidity();
	}

	updateValidatorsForFilterType(group: AbstractControl): void {
		const typeCtrl = group.get('type');
		if (!typeCtrl) {
			throw Error(`could not find control 'type'`);
		}

		// Empty and NotEmpty are special filters - their type already defines everything
		// Will be remapped before saving (NotEmpty is a fakefilter in FE - only Empty exists in BE)
		if (typeCtrl.value === FilterType.Empty) {
			this.removeRequiredFromControl('compareValue', group);
		} else {
			// could have been removed -> re-add it to be safe
			this.addRequiredToControl('compareValue', group);
		}
	}

// JSON-eMail-Daten vom Backend. =========================================================================================================

	public mapEmailDataToPreview(previewData: PreviewDataFormEntry[]): PreviewDataFormEntry[] {
		const emailFieldMap: { [key: string]: string } = {
			subject: 'Betreff',
			from: 'Absender',
			body_excerpt: 'Inhalt',
			attachments: 'Anhänge',
			to: 'Empfänger'
		};

		const mappedData: PreviewDataFormEntry[] = [];

		previewData.forEach((entry: PreviewDataFormEntry) => {
			if (emailFieldMap[entry.key]) {
				let exampleValue = entry.value;

				if (typeof exampleValue === 'string' && exampleValue.length > 150) {
					exampleValue = exampleValue.substring(0, 150) + '...';
				}
				mappedData.push({
					key: emailFieldMap[entry.key],
					value: exampleValue ? exampleValue.toString() : 'Kein Wert'
				});
			}
		});

		return mappedData.length > 0 ? mappedData : previewData;
	}
}
